import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  EventEmitter,
  HostListener,
  input,
  Input,
  Output,
} from '@angular/core';
import { Commands, Search } from '@local/client-contracts';
import { PopupRef, UiIconModel } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuComponent, ContextMenuData, ContextMenuItem } from '@shared/components';
import { ContextMenuService } from '@shared/components/context-menu/context-menu.service';
import { stopEvent } from '@shared/utils/elements-util';
import { getIconModel } from '@shared/utils/set-icon.util';
import { WikiCardsService } from 'src/app/bar/services/wikis/wiki-cards.service';
import { InvokeCommand } from '../../../models/invoke-command.model';
import { SearchResults, TelemetryTrigger } from '../../../models/results-types';
import { getPrimaryActionClick, isExternalSourceItem, isPreviewClickAction, isWikiCard } from '../../../utils/results.util';
import { ResultsWidth } from '../short-result-resources.component';
@UntilDestroy()
@Component({
  selector: 'short-result-resource',
  templateUrl: './short-result-resource.component.html',
  styleUrls: ['./short-result-resource.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShortResultResourceComponent {
  private readonly EXTERNAL_FALLBACK_ICON = 'assets/icons/network.svg';
  activeContextMenu: PopupRef<ContextMenuComponent, ContextMenuData>;

  resourceModel = computed<Search.ResultResourceItem>(() => {
    return this.model() as Search.ResultResourceItem;
  });

  iconModel = computed<UiIconModel>(() => {
    if (this.resourceModel()?.resource?.appId === 'wiki') {
      return getIconModel(this.model()?.view?.iconOverlay);
    }
    return getIconModel(this.model()?.view?.icon);
  });

  fallbackIcon = computed(() => {
    if (isExternalSourceItem(this.model())) {
      return this.EXTERNAL_FALLBACK_ICON;
    }
    return null;
  });

  model = input<Search.ResultItem>();

  @Input() referenceIndex: number;
  @Input() additionalResources: Search.ResultItem[];
  @Input() showAllResourcesClicked: boolean;
  @Input() isSingleResource: boolean;
  @Input() resourceActiveIndex: number;
  @Input() searchId: string;
  @Input() seeAll: boolean;
  @Input() hasTwoResourcesOverflow: boolean;

  @Output() invoke = new EventEmitter<InvokeCommand>();
  @Output() menuActive = new EventEmitter<boolean>();
  @Output() onAdditionalResourcesClick = new EventEmitter<boolean>();

  @HostListener('contextmenu', ['$event'])
  async onRightClick(event: MouseEvent) {
    if (this.model()) {
      this.menuActive.emit(true);
      this.openContextMenu(event);
    }
    stopEvent(event);
  }

  @HostListener('click', ['$event'])
  async onClick(event: MouseEvent) {
    this.menuActive.emit(true);
    this.openResourceUrlOrPreview(event);
    stopEvent(event);
  }

  constructor(
    protected cdr: ChangeDetectorRef,
    private contextMenuService: ContextMenuService,
    private wikiCardsService: WikiCardsService
  ) {}

  showOverlayPanel($event, actualTarget, overlayPanel) {
    // Prevent the overlay from opening immediately after clicking 'See All' due to poor appearance.
    if (!this.showAllResourcesClicked) {
      overlayPanel.show($event, actualTarget);
    }
    this.showAllResourcesClicked = false;
  }

  clickAdditionalResources() {
    this.onAdditionalResourcesClick.emit(true);
  }

  private openResourceUrlOrPreview(event: MouseEvent): void {
    if (getPrimaryActionClick((this.model() as SearchResults)?.action) === 'preview') {
      this.onPreviewClick();
      event.stopPropagation();
    } else {
      this.openUrl('mouse_click');
    }
  }
  private async onPreviewClick(): Promise<void> {
    const eventInfo = this.createEventInfo('preview', 'open_preview', (<Commands.OpenUrl>this.model().view?.title?.onClick)?.url);
    this.onInvoke({
      command: this.buildPreviewCommand(),
      trigger: 'mouse_click',
      eventInfo,
    });
  }
  private createEventInfo(target: string, label: string, url: string): any {
    return {
      target,
      label,
      location: { title: url },
      resources: [{ id: this.model().id, list: 'search_bar_results', type: this.model().type, position: 0 }],
    };
  }
  openUrl(trigger: TelemetryTrigger): void {
    const onClickCommand = this.model()?.view?.title?.onClick;
    if (onClickCommand) {
      this.onInvoke({
        command: onClickCommand,
        trigger,
      });
    }
  }
  async openContextMenu(event: MouseEvent) {
    const openAt: { x: number; y: number } = {
      x: event.x,
      y: event?.y,
    };
    const items = this.buildContextMenuItems();
    if (!items?.length) {
      return;
    }
    this.activeContextMenu = this.contextMenuService.open(
      openAt,
      {
        items,
        onInvoke: (item: InvokeCommand) => this.handleContextMenuCommand(item),
      },
      { position: 'below' }
    );
    this.activeContextMenu.destroy$.pipe(untilDestroyed(this)).subscribe(() => {
      this.menuActive.emit(false);
      this.cdr.markForCheck();
    });
  }
  private buildContextMenuItems(): ContextMenuItem[] {
    const resouceItem = this.model() as Search.ResultResourceItem;
    if (!resouceItem.resource) {
      return;
    }
    let resourceUrl = (<Commands.OpenUrl>this.model()?.view?.title?.onClick)?.url;
    if (isWikiCard(this.model())) {
      resourceUrl = this.wikiCardsService.getCardUrl(resouceItem?.resource?.data?.title, resouceItem?.resource?.data?.id, true);
    }
    const items: ContextMenuItem[] = [];
    if (isPreviewClickAction((this.model() as SearchResults)?.action)) {
      items.push({
        id: 'preview',
        text: 'Preview',
        command: { type: 'preview' },
        icon: { type: 'font-icon', value: 'icon-eye' },
      });
    }
    if (resourceUrl) {
      items.push({
        id: 'open',
        text: 'Open',
        icon: { type: 'font-icon', value: 'icon-launch' },
        data: {},
        command: { type: 'open-url', url: resourceUrl } as any,
      });
      items.push({
        id: 'copy-url',
        text: 'Copy URL',
        icon: { type: 'font-icon', value: 'icon-link2' },
        data: {},
        command: { type: 'copy-clipboard', value: resourceUrl } as Commands.DynamicCommand,
      });
    }
    return items;
  }
  private buildPreviewCommand(): Commands.Preview<SearchResults> {
    return {
      type: 'preview',
      previewable: false,
      state: 'popup',
      model: this.model(),
      selectedIndex: 0,
    };
  }
  private buildInvokeCommand(): InvokeCommand {
    return {
      command: this.buildPreviewCommand(),
      invokerItem: this.model(),
      trigger: 'context_menu_click',
    };
  }
  private handleContextMenuCommand(command: ContextMenuItem) {
    if (command.id === 'preview') {
      this.onInvoke(this.buildInvokeCommand());
    } else {
      this.onInvoke(command as InvokeCommand);
    }
  }

  async onInvoke(data: InvokeCommand): Promise<void> {
    this.invoke.emit({ ...data, invokerItem: this.model(), trigger: 'context_menu_click' });
  }
}
